
import { textSearch } from "@/assets/ts/_utils";
import { toLocaleDateString } from "@/assets/ts/_utils/formatters";
import JsonExcel from "@/components/widgets/tables/JsonExcel.vue";
import { InscripcionEstado } from "@/enums";
import { Inscripcion } from "@/store/models";
import { CursosModule } from "@/store/modules";
import { ArrowDown } from "@element-plus/icons-vue";
import moment from "moment";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";

export default defineComponent({
  name: "servicios-listado",
  components: { "download-excel": JsonExcel, ArrowDown },
  props: {
    inscripciones: Object as () => Inscripcion[],
  },

  methods: {
    formatLongDate(fecha) {
      return moment(fecha).locale("es").format("L");
    },
    formatHour(fecha) {
      return moment(fecha).format("HH:mm");
    },
  },
  setup(props) {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const module = getModule(CursosModule, store);

    const csvLabels = {
      createdOn: t("csv.listados.inscripciones.fecha"),
      alumno: t("csv.listados.inscripciones.alumno"),
      dni: t("csv.listados.inscripciones.dni"),
      email: t("csv.listados.inscripciones.email"),
      telefono: t("csv.listados.inscripciones.telefono"),
      localidad: t("csv.listados.inscripciones.localidad"),
      provincia: t("csv.listados.inscripciones.provincia"),
      modalidad: t("csv.listados.inscripciones.modalidad"),
      estado: t("csv.listados.inscripciones.estado"),
      perfil: t("csv.listados.inscripciones.perfil"),
      nombreEmpresa: t("csv.listados.inscripciones.nombreEmpresa"),
      localidadEmpresa: t("csv.listados.inscripciones.localidadEmpresa"),
      cifEmpresa: t("csv.listados.inscripciones.cifEmpresa"),
      sectorEmpresa: t("csv.listados.inscripciones.sectorEmpresa"),
      empleadosEmpresa: t("csv.listados.inscripciones.empleadosEmpresa"),
      puesto: t("csv.listados.inscripciones.puesto"),
      entidad: t("csv.listados.inscripciones.entidad"),
    };

    const csvFields = [
      "createdOn",
      "alumno",
      "dni",
      "email",
      "telefono",
      "localidad",
      "provincia",
      "modalidad",
      "estado",
      "perfil",
      "nombreEmpresa",
      "localidadEmpresa",
      "cifEmpresa",
      "sectorEmpresa",
      "empleadosEmpresa",
      "puesto",
      "entidad",
    ];
    const csvName = "inscripcionesListado" + new Date().getTime() + ".xlsx";

    const search = ref("");

    const sortInscripcionEstado = function (a, b) {
      let primero = a.estado;
      let segundo = b.estado;
      if (primero > segundo) {
        return 1;
      }
      if (primero < segundo) {
        return -1;
      }
      return 0;
    };

    const loading = ref(false);

    const handleEdit = (index, row) => {
      router.push(`/listados/servicios-programados/${row.id}`);
    };
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    });

    const onInscrito = async (row) => {
      const idInscripcion = row.id;
      const estado = "INSCRITO";

      const plazasTotales = module.curso?.plazas;
      if (!plazasTotales) {
        return;
      }
      // Contabilizamos todas las plazas con estado INSCRITO o FINALIZADO y también la plaza del propio alumno para que permita cambio de INSCRITO a FINALIZADO con todas las plazas completas

      const plazasUtilizadas =
        module.curso?.inscripciones
          ?.filter(
            (i) =>
              i.estado === InscripcionEstado.inscrito ||
              i.estado === InscripcionEstado.finalizado
          )
          .filter((u) => u.id !== row.id).length ?? 0;

      if (plazasTotales <= plazasUtilizadas) {
        Swal.fire({
          html: t("dialogs.ocuparPlazaInscripcion.limitePlazas.body", {
            plazasTotales: plazasTotales,
            plazasUtilizadas: plazasUtilizadas,
          }),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
        return;
      }

      loading.value = true;
      try {
        await module
          .updateInscripcion({
            id: idInscripcion as string,
            inscripcion: {
              estado: estado,
            },
          })
          .then(async () => {
            if (!module.errors?.error) {
              const token = route.params.id;
              await module.fetchById({
                id: token as string,
              });
              Swal.fire({
                text: t("formularios.inscripcion.cambioEstado.mensaje.exito"),
                icon: "success",
                buttonsStyling: false,
                confirmButtonText: t("dialogs.okButton"),
                customClass: {
                  confirmButton: "btn btn-primary",
                },
              });
            } else {
              swalWithBootstrapButtons.fire(
                t("dialogs.error"),
                t("formularios.inscripcion.cambioEstado.mensaje.error"),
                "error"
              );
            }
          });
      } catch (err) {
        console.error("Error aprobar", err);
        Swal.fire({
          text: t("formularios.inscripcion.cambioEstado.mensaje.error"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      } finally {
        loading.value = false;
      }
    };
    const onFinalizado = async (row) => {
      const idInscripcion = row.id;
      const estado = "FINALIZADO";

      const plazasTotales = module.curso?.plazas;
      if (!plazasTotales) {
        return;
      }
      // Contabilizamos todas las plazas con estado INSCRITO o FINALIZADO y también la plaza del propio alumno para que permita cambio de INSCRITO a FINALIZADO con todas las plazas completas
      const plazasUtilizadas =
        module.curso?.inscripciones
          ?.filter(
            (i) =>
              i.estado === InscripcionEstado.inscrito ||
              i.estado === InscripcionEstado.finalizado
          )
          .filter((u) => u.id !== row.id).length ?? 0;

      if (plazasTotales <= plazasUtilizadas) {
        Swal.fire({
          html: t("dialogs.ocuparPlazaInscripcion.limitePlazas.body", {
            plazasTotales: plazasTotales,
            plazasUtilizadas: plazasUtilizadas,
          }),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
        return;
      }

      loading.value = true;
      try {
        await module
          .updateInscripcion({
            id: idInscripcion as string,
            inscripcion: {
              estado: estado,
            },
          })
          .then(async () => {
            if (!module.errors?.error) {
              const token = route.params.id;
              await module.fetchById({
                id: token as string,
              });
              Swal.fire({
                text: t("formularios.inscripcion.cambioEstado.mensaje.exito"),
                icon: "success",
                buttonsStyling: false,
                confirmButtonText: t("dialogs.okButton"),
                customClass: {
                  confirmButton: "btn btn-primary",
                },
              });
            } else {
              swalWithBootstrapButtons.fire(
                t("dialogs.error"),
                t("formularios.inscripcion.cambioEstado.mensaje.error"),
                "error"
              );
            }
          });
      } catch (err) {
        console.error("Error aprobar", err);
        Swal.fire({
          text: t("formularios.inscripcion.cambioEstado.mensaje.error"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      } finally {
        loading.value = false;
      }
    };
    const onAbandono = async (row) => {
      const idInscripcion = row.id;
      const estado = "ABANDONO";

      loading.value = true;
      try {
        await module
          .updateInscripcion({
            id: idInscripcion as string,
            inscripcion: {
              estado: estado,
            },
          })
          .then(async () => {
            if (!module.errors?.error) {
              const token = route.params.id;
              await module.fetchById({
                id: token as string,
              });
              Swal.fire({
                text: t("formularios.inscripcion.cambioEstado.mensaje.exito"),
                icon: "success",
                buttonsStyling: false,
                confirmButtonText: t("dialogs.okButton"),
                customClass: {
                  confirmButton: "btn btn-primary",
                },
              });
            } else {
              swalWithBootstrapButtons.fire(
                t("dialogs.error"),
                t("formularios.inscripcion.cambioEstado.mensaje.error"),
                "error"
              );
            }
          });
      } catch (err) {
        console.error("Error aprobar", err);
        Swal.fire({
          text: t("formularios.inscripcion.cambioEstado.mensaje.error"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      } finally {
        loading.value = false;
      }
    };
    const onCancelado = async (row) => {
      const idInscripcion = row.id;
      const estado = "CANCELADO";

      loading.value = true;
      try {
        await module
          .updateInscripcion({
            id: idInscripcion as string,
            inscripcion: {
              estado: estado,
            },
          })
          .then(async () => {
            if (!module.errors?.error) {
              const token = route.params.id;
              await module.fetchById({
                id: token as string,
              });
              Swal.fire({
                text: t("formularios.inscripcion.cambioEstado.mensaje.exito"),
                icon: "success",
                buttonsStyling: false,
                confirmButtonText: t("dialogs.okButton"),
                customClass: {
                  confirmButton: "btn btn-primary",
                },
              });
            } else {
              swalWithBootstrapButtons.fire(
                t("dialogs.error"),
                t("formularios.inscripcion.cambioEstado.mensaje.error"),
                "error"
              );
            }
          });
      } catch (err) {
        console.error("Error aprobar", err);
        Swal.fire({
          text: t("formularios.inscripcion.cambioEstado.mensaje.error"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      } finally {
        loading.value = false;
      }
    };

    const onListaDeEspera = async (row) => {
      const idInscripcion = row.id;
      const estado = "LISTA_DE_ESPERA";

      loading.value = true;
      try {
        await module
          .updateInscripcion({
            id: idInscripcion as string,
            inscripcion: {
              estado: estado,
            },
          })
          .then(async () => {
            if (!module.errors?.error) {
              const token = route.params.id;
              await module.fetchById({
                id: token as string,
              });
              Swal.fire({
                text: t("formularios.inscripcion.cambioEstado.mensaje.exito"),
                icon: "success",
                buttonsStyling: false,
                confirmButtonText: t("dialogs.okButton"),
                customClass: {
                  confirmButton: "btn btn-primary",
                },
              });
            } else {
              swalWithBootstrapButtons.fire(
                t("dialogs.error"),
                t("formularios.inscripcion.cambioEstado.mensaje.error"),
                "error"
              );
            }
          });
      } catch (err) {
        console.error("Error aprobar", err);
        Swal.fire({
          text: t("formularios.inscripcion.cambioEstado.mensaje.error"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      } finally {
        loading.value = false;
      }
    };

    const verSolicitud = (row) => {
      router.push(`/listados/cursos-inscripciones/${row.id}`);
    };

    const registros = computed(() => {
      return props.inscripciones?.map((row) => {
        const perfilTexto = t(`perfiles.${row.perfil}`);
        return {
          ...row,
          alumno: row.nombre + " " + row.apellidos,
          modalidad:
            row.videoconferencia && row.presencial
              ? "Aula Virtual / Presencial"
              : row.videoconferencia
              ? "Aula Virtual"
              : "Presencial",
          perfilTexto: perfilTexto,
          //...
        };
      });
    });
    const inscripcionesActivas = computed(() => {
      return (
        props.inscripciones?.filter(
          (ele) =>
            ele.estado === InscripcionEstado.inscrito ||
            ele.estado === InscripcionEstado.finalizado
        ).length || 0
      );
    });
    const countInscripciones = computed(() => {
      return props.inscripciones?.length || 0;
    });

    const getTagType = (estado: string) => {
      switch (estado) {
        case InscripcionEstado.inscrito:
        case InscripcionEstado.finalizado:
          return "success";
        case InscripcionEstado.listaDeEspera:
          return "primary";
        default:
          return "danger";
      }
    };

    return {
      inscripcionesListado: computed(() =>
        registros.value?.filter((data) => {
          const modalidad =
            data.videoconferencia && data.presencial
              ? "Aula Virtual / Presencial"
              : data.videoconferencia
              ? "Aula Virtual"
              : "Presencial";
          return (
            !search.value ||
            textSearch(data.nombre, search.value) ||
            textSearch(data.apellidos, search.value) ||
            textSearch(data.dni, search.value) ||
            textSearch(data.telefono, search.value) ||
            textSearch(data.email, search.value) ||
            textSearch(data.localidad, search.value) ||
            textSearch(modalidad, search.value) ||
            textSearch(t(`inscripciones.estado.${data.estado}`), search.value)
          );
        })
      ),
      csvLabels,
      csvFields,
      csvName,
      loading,
      search,
      sortInscripcionEstado,
      handleEdit,
      onInscrito,
      onFinalizado,
      onAbandono,
      onCancelado,
      onListaDeEspera,
      verSolicitud,
      InscripcionEstado,
      toLocaleDateString,
      inscripcionesActivas,
      countInscripciones,
      getTagType,
    };
  },
});
